// Tiny localStorage persistence layer for admin list filters (2026-08-06
// design, Task 2). Every access is wrapped in try/catch: private mode throws
// on ANY localStorage touch, and the node test environment has no
// localStorage at all — a filter that fails to persist must never take the
// page down with it.

/**
 * Read + validate a stored filter. `parse` is a caller-supplied validator, not
 * a bare JSON.parse cast: a value written by an earlier release can outlive
 * several deploys, so an unrecognised shape is discarded (null), never fed to
 * the page as a filter object.
 */
export function readStoredFilter<T>(key: string, parse: (raw: unknown) => T | null): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return null;
    return parse(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeStoredFilter(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore — private mode / storage disabled */
  }
}

export function clearStoredFilter(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore — private mode / storage disabled */
  }
}

/**
 * Intersection of `selected` with `available`, in `available` order — the same
 * determinism rule orderedSelected (components/searchableMultiSelect.ts)
 * applies to emitted selections. Used to reconcile a restored filter against
 * the live option list once its source has resolved.
 */
export function pruneToOptions(selected: string[], available: string[]): string[] {
  const wanted = new Set(selected);
  return available.filter((v) => wanted.has(v));
}
